import React, { useMemo } from 'react'
import { Icon } from '../../atoms/Icon'
import { Loading } from '../../atoms/Loading'
import { HistoryTabProps } from './HistoryTab.types'
import {
  formatSwissPrice,
  formatSwissPricePerSqm,
  getDateLocale,
} from './events/formatUtils'
import {
  HistoryEvent,
  isPermitEvent,
  isTransactionEvent,
  isRentalEvent,
  isSaleEvent,
  PermitEventComponent,
  TransactionEventComponent,
  RentalEventComponent,
  SaleEventComponent,
} from './events'
import { EventDetailPanel } from './details'
import {
  HistoryContainer,
  FiltersBar,
  EventFilters,
  FilterPillGroup,
  FilterPill,
  FilterActionButton,
  FilterIcon,
  FilterCount,
  FilterSinceYear,
  SortButton,
  TimelineContainer,
  Timeline,
  TimelineEvent,
  TimelineMarker,
  EventDate,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyText,
  LoadingContainer,
  ErrorContainer,
  ErrorTitle,
  ErrorText,
  EventWrapper,
} from './HistoryTab.styles'

const defaultFormatDate = (dateString: string | Date) => {
  const date = new Date(dateString)
  return date.toLocaleDateString(getDateLocale(), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

const renderEvent = (
  event: HistoryEvent,
  translations?: any,
  formatPrice?: (price: number) => string,
  formatPricePerSqm?: (price: number) => string,
  formatDate?: (date: string | Date) => string
) => {
  if (isPermitEvent(event)) {
    return (
      <PermitEventComponent
        event={event}
        translations={translations?.permit}
        formatPrice={formatPrice}
      />
    )
  }

  if (isTransactionEvent(event)) {
    return (
      <TransactionEventComponent
        event={event}
        translations={translations?.transaction}
        formatPrice={formatPrice}
        formatPricePerSqm={formatPricePerSqm}
        formatDate={formatDate}
      />
    )
  }

  if (isRentalEvent(event)) {
    return (
      <RentalEventComponent
        event={event}
        translations={translations?.rental}
        formatPrice={formatPrice}
        formatPricePerSqm={formatPricePerSqm}
        formatDate={formatDate}
      />
    )
  }

  if (isSaleEvent(event)) {
    return (
      <SaleEventComponent
        event={event}
        translations={translations?.sale}
        formatPrice={formatPrice}
        formatPricePerSqm={formatPricePerSqm}
      />
    )
  }

  // Fallback for unknown event types
  return null
}

export const HistoryTab: React.FC<HistoryTabProps> = ({
  events,
  loading = false,
  error = null,
  filters = [],
  onFilterToggle,
  onEventClick,
  selectedEvent,
  onCloseDetail,
  disableInlineDetail = false,
  filterActions = [],
  sortOrder = 'desc',
  onSortToggle,
  emptyStateTitle,
  emptyStateText,
  loadingTitle,
  errorTitle,
  formatPrice = formatSwissPrice,
  formatPricePerSqm = formatSwissPricePerSqm,
  formatDate = defaultFormatDate,
  translations,
  className,
  loadTransaction,
  loadListing,
  getParticipantsByRoleGrouped,
}) => {
  const filterActionByType = useMemo(
    () => new Map(filterActions.map((action) => [action.type, action])),
    [filterActions]
  )

  // Filter and sort events
  const filteredEvents = useMemo(() => {
    let result = events

    if (filters.length > 0) {
      const activeFilters = filters.filter((f) => f.active)

      if (activeFilters.length > 0 && activeFilters.length < filters.length) {
        const activeTypes = activeFilters.map((f) => f.type)
        result = result.filter((event) => activeTypes.includes(event.type))
      }
    }

    return [...result].sort((a, b) => {
      const diff = new Date(a.date).getTime() - new Date(b.date).getTime()
      return sortOrder === 'asc' ? diff : -diff
    })
  }, [events, filters, sortOrder])

  if (loading) {
    return (
      <HistoryContainer className={className}>
        <LoadingContainer>
          <Loading size="medium" />
          <span style={{ marginLeft: '8px' }}>
            {loadingTitle || translations?.loading || 'Loading...'}
          </span>
        </LoadingContainer>
      </HistoryContainer>
    )
  }

  if (error) {
    return (
      <HistoryContainer className={className}>
        <ErrorContainer>
          <ErrorTitle>
            {errorTitle || translations?.error || 'Error'}
          </ErrorTitle>
          <ErrorText>{error}</ErrorText>
        </ErrorContainer>
      </HistoryContainer>
    )
  }

  return (
    <HistoryContainer className={className}>
      {(filters.length > 0 || onSortToggle) && (
        <FiltersBar>
          {filters.length > 0 && onFilterToggle && (
            <EventFilters>
              {filters.map((filter) => {
                const action = filterActionByType.get(filter.type)

                const filterPill = (
                  <FilterPill
                    key={filter.type}
                    $active={filter.active}
                    $type={filter.type}
                    $hasAction={Boolean(action)}
                    onClick={() => onFilterToggle(filter.type)}
                    aria-label={filter.label}
                  >
                    <FilterIcon>
                      <Icon name={filter.icon as any} size="small" />
                    </FilterIcon>
                    <span>{filter.label}</span>
                    <FilterCount $active={filter.active}>
                      {filter.count}
                    </FilterCount>
                    {filter.sinceYear && (
                      <FilterSinceYear>
                        {translations?.since || 'Since'} {filter.sinceYear}
                      </FilterSinceYear>
                    )}
                  </FilterPill>
                )

                if (!action) return filterPill

                return (
                  <FilterPillGroup key={filter.type}>
                    {filterPill}
                    <FilterActionButton
                      $active={filter.active}
                      $type={filter.type}
                      aria-label={action.label}
                      title={action.title || action.label}
                      disabled={action.disabled}
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()

                        if (!action.disabled) {
                          action.onClick(filter.type)
                        }
                      }}
                    >
                      <Icon
                        name={(action.icon || 'external-link') as any}
                        size="small"
                      />
                    </FilterActionButton>
                  </FilterPillGroup>
                )
              })}
            </EventFilters>
          )}
          {onSortToggle && (
            <SortButton $desc={sortOrder === 'desc'} onClick={onSortToggle}>
              <Icon name="arrow-up-down" size="small" />
              {sortOrder === 'desc'
                ? translations?.sortNewest || 'Newest'
                : translations?.sortOldest || 'Oldest'}
            </SortButton>
          )}
        </FiltersBar>
      )}

      <TimelineContainer>
        {selectedEvent && onCloseDetail && !disableInlineDetail ? (
          <EventDetailPanel
            event={selectedEvent}
            onClose={onCloseDetail}
            formatPrice={formatPrice}
            formatPricePerSqm={formatPricePerSqm}
            formatDate={formatDate}
            translations={translations}
            loadTransaction={loadTransaction}
            loadListing={loadListing}
            getParticipantsByRoleGrouped={getParticipantsByRoleGrouped}
          />
        ) : filteredEvents.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <Icon name="mail" size="large" />
            </EmptyIcon>
            <EmptyTitle>
              {emptyStateTitle ||
                translations?.noEvents ||
                'No history events found'}
            </EmptyTitle>
            {(emptyStateText || translations?.adjustFilters) && (
              <EmptyText>
                {emptyStateText || translations?.adjustFilters}
              </EmptyText>
            )}
          </EmptyState>
        ) : (
          <Timeline>
            {filteredEvents.map((event) => (
              <TimelineEvent key={event.id} $type={event.type} $visible={true}>
                <TimelineMarker $type={event.type} />
                <EventDate>{formatDate(event.date)}</EventDate>
                <EventWrapper onClick={() => onEventClick?.(event)}>
                  {renderEvent(
                    event,
                    translations,
                    formatPrice,
                    formatPricePerSqm,
                    formatDate
                  )}
                </EventWrapper>
              </TimelineEvent>
            ))}
          </Timeline>
        )}
      </TimelineContainer>
    </HistoryContainer>
  )
}
